import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppSettings } from '../../../app.settings';
import { JsonEditConfigService } from '../../../services/json-edit-config/json-edit-config.service';
import { TabStorageService } from '../../../services/tabstorage/tabstorage.service';

interface ConfigInterface {
  app_name: string;
  auth_key: string;
}

interface ResponseJson {
  currentEnv: any[];
  envToCompare: any[];
}

const ENV_LIST = [
  { key: 'Development', value: 'dev' },
  { key: 'QA', value: 'qa' },
  { key: 'Staging', value: 'staging' },
  { key: 'Production', value: 'prod' },
  { key: 'Jobs', value: 'job' },
];

@Component({
  selector: 'app-json-config',
  templateUrl: './json-config.component.html',
  styleUrls: ['./json-config.component.scss'],
})
export class JsonConfigComponent implements OnInit {
  form = this.formBuilder.group({
    jsonConfigForm: this.formBuilder.control<any>({}),
  });

  selectedConfig: string | null = null;
  envToCompare: string | null = null;
  envConfigList: ConfigInterface[] = [];
  env = AppSettings.getCanonicalConfigEnv();
  envList = ENV_LIST;
  tokenHeader: HttpHeaders | null = null;
  keysExclusiveToCurrentEnv: any[] = [];
  keysExclusiveToComparedEnv: any[] = [];
  loading = false;

  constructor(
    private formBuilder: FormBuilder,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private jsonEditConfigService: JsonEditConfigService,
  ) {}

  ngOnInit(): void {
    if (this.checkUserToken()) {
      this.getConfigToken();
    }
    this.envList = this.envList.filter((env) => env.value !== this.env);
  }

  checkUserToken(): boolean {
    const userToken = TabStorageService.getStorage(TabStorageService.JWT_TOKEN);
    return !!userToken;
  }

  async getConfigToken(): Promise<void> {
    this.loading = true;
    try {
      const jwt = TabStorageService.getStorage(TabStorageService.JWT_TOKEN);
      const authHeaders = jwt ? new HttpHeaders({ Authorization: `Bearer ${jwt}` }) : undefined;
      const res: any = await this.http
        .get(`${AppSettings.getEndpoint()}auth/global-configs?scope=read,write`, authHeaders ? { headers: authHeaders } : {})
        .toPromise();
      this.tokenHeader = new HttpHeaders({
        'Content-Type': 'application/json',
        'x-global-configs-auth': res.token,
      });
      await this.getConfigEnvList();
    } catch (error) {
      console.error('Error getting config token', error);
      this.snackBar.open('Error loading global config token', 'Close', { duration: 2000 });
    } finally {
      this.loading = false;
    }
  }

  async getConfigEnvList(): Promise<void> {
    if (!this.tokenHeader) {
      return;
    }
    try {
      const response = await this.http
        .get<ConfigInterface[]>(`${AppSettings.getConfigEndpoint()}/env/${AppSettings.getCanonicalConfigEnv()}`, {
          headers: this.tokenHeader,
        })
        .toPromise();
      this.envConfigList = response || [];
    } catch (error) {
      console.error('Error loading config list', error);
      this.snackBar.open('Error loading config list', 'Close', { duration: 2000 });
    }
  }

  async onSelectedConfig(): Promise<void> {
    this.keysExclusiveToCurrentEnv = [];
    this.keysExclusiveToComparedEnv = [];
    if (!this.selectedConfig) {
      this.form.patchValue({ jsonConfigForm: {} });
      return;
    }
    await this.getJsonConfigData();
  }

  async getJsonConfigData(): Promise<void> {
    if (!this.tokenHeader || !this.selectedConfig) {
      return;
    }
    this.loading = true;
    try {
      const response = await this.http
        .get(`${AppSettings.getConfigEndpoint()}/${this.selectedConfig}`, {
          headers: this.tokenHeader,
        })
        .toPromise();
      this.form.patchValue({ jsonConfigForm: response || {} });
    } catch (error) {
      console.error('Error loading JSON config', error);
      this.snackBar.open('Error loading JSON config', 'Close', { duration: 2000 });
    } finally {
      this.loading = false;
    }
  }

  async saveForm(): Promise<void> {
    if (!this.tokenHeader || !this.selectedConfig) {
      this.snackBar.open('Error! No configuration to update!', 'Close', { duration: 2000 });
      return;
    }
    this.loading = true;
    try {
      await this.http
        .put(`${AppSettings.getConfigEndpoint()}/${this.selectedConfig}`, this.form.get('jsonConfigForm')?.value || {}, {
          headers: this.tokenHeader,
        })
        .toPromise();
      this.snackBar.open('JSON updated successfully', 'Close', { duration: 2000 });
    } catch (error: any) {
      console.error('Error updating JSON config', error);
      this.snackBar.open('Error when trying to update the JSON', 'Close', { duration: 2000 });
    } finally {
      this.loading = false;
    }
  }

  async compareEnvs(): Promise<void> {
    if (!this.envToCompare || !this.tokenHeader) {
      return;
    }
    this.loading = true;
    try {
      const response = await this.http
        .get<ResponseJson>(`${AppSettings.getConfigEndpoint()}/compare/${this.envToCompare}`, {
          headers: this.tokenHeader,
        })
        .toPromise();
      if (response) {
        this.keysExclusiveToCurrentEnv = response.currentEnv || [];
        this.keysExclusiveToComparedEnv = response.envToCompare || [];
        if (this.keysExclusiveToCurrentEnv.length === 0 && this.keysExclusiveToComparedEnv.length === 0) {
          this.snackBar.open('No differences found between the environments', 'Close', { duration: 2000 });
        }
      }
    } catch (error) {
      console.error('Error comparing environments', error);
      this.snackBar.open('Error when trying to compare the envs', 'Close', { duration: 2000 });
    } finally {
      this.loading = false;
    }
  }

  async redeployBackend(jobs = false): Promise<void> {
    const target = jobs ? 'jobs' : 'api';

    try {
      const res = await this.jsonEditConfigService.redeployBackend(target).toPromise();
      this.snackBar.open(res?.message || 'Redeploy requested', 'Close', { duration: 2000 });
    } catch (error: any) {
      this.snackBar.open(`Error when trying to redeploy: ${this.getRedeployErrorMessage(error, jobs)}`, 'Close', { duration: 5000 });
    }
  }

  private getRedeployErrorMessage(error: any, jobs: boolean): string {
    if (jobs && error?.status === 0) {
      return 'Prod jobs redeploy must be requested through the prod API endpoint.';
    }

    return error?.error?.message || error?.error?.error || error?.message || 'Unknown error';
  }

  cancel(): void {
    this.form.patchValue({ jsonConfigForm: {} });
    this.selectedConfig = null;
  }

  getEnvKey(): string {
    const key = this.envList.find((env) => env.value === this.envToCompare);
    return key?.key || this.envToCompare || '';
  }
}
